import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getEmailEventDetail } from "../redux/emailEventSlice";
import { deleteEmailEvent } from "../services/emailEventService";
import { toast } from "react-toastify";

import Card from "../components/UI/Card";
import Button from "../components/UI/Button";
import Modal from "../components/UI/Modal";
import StatusBadge from "../components/UI/StatusBadge";
import SummaryCard from "../components/UI/SummaryCard";
import EmailRecipientTable from "../components/email/EmailRecipientTable";
import ServerPagination from "../components/ServerPagination";
import PageSizeSelector from "../components/PageSizeSelector";

const EmailEventDetail = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { eventDetail, loading } = useSelector(
    (state) => state.emailEvents
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const refresh = () => {
    dispatch(getEmailEventDetail({
        eventId,
        params: {
          page: currentPage,
          page_size: pageSize,
        },
      }));
  };

  useEffect(() => {
    refresh();
  }, [eventId, currentPage, pageSize]);

  if (loading || !eventDetail)
    return <div className="p-6">Loading...</div>;

  const event = eventDetail.event;
  const recipientData = eventDetail.recipients;
  const recipients = eventDetail.recipients?.results || [];

  const successRate = event.total_recipients
    ? ((event.total_success / event.total_recipients) * 100).toFixed(1)
    : "0.0";

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteEmailEvent(eventId);
      toast.success("Event deleted successfully");
      navigate("/email-events");
    } catch {
      toast.error("Failed to delete event");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="p-6">
      <Card>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Email Event Detail
            </h2>
            <p className="text-sm text-gray-500">
              {event.event_name} • {event.email_category}
            </p>
          </div>

          <Button
            variant="danger"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Event
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <SummaryCard label="Recipients" value={event.total_recipients} />
          <SummaryCard label="Success" value={event.total_success} />
          <SummaryCard label="Failed" value={event.total_failed} />
          <SummaryCard label="Success %" value={`${successRate}%`} />
          <SummaryCard
            label="Time"
            value={`${(event.execution_time_ms / 1000).toFixed(2)}s`}
          />
        </div>

        <div className="mb-6">
          <StatusBadge status={event.status} />
        </div>

        {/* Failed Recipients */}
        <EmailRecipientTable
          recipients={recipients}
          eventId={eventId}
          refresh={refresh}
        />

             {/* Page Size Selector */}
        <div className="mt-4">
          <PageSizeSelector
            pageSize={pageSize}
            onPageSizeChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Pagination */}
        {recipientData && recipientData.total_pages > 1 && (
          <ServerPagination
            currentPage={recipientData.current_page}
            totalPages={recipientData.total_pages}
            onPageChange={setCurrentPage}
            totalCount={recipientData.count}
            pageSize={recipientData.page_size}
          />
        )}

      </Card>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        title="Delete Email Event"
        onClose={() => setShowDeleteModal(false)}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>

            <Button
              variant="danger"
              loading={deleting}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        This will permanently delete this event and all associated recipients.
      </Modal>
    </div>
  );
};

export default EmailEventDetail;